import { hashArray } from './hash.js';

/**
 *  $ - find the first HTMLElement satisfying the given selector within the given scope
 *
 *  @param {String} selector - CSS selector
 *  @param {HTMLElement} container - scope of search
 *  @return {HTMLElement|null} - first result of search, if any
 */
export const $ = (selector, container = document) => container.querySelector(selector);

/**
 *  $$ - find an array of HTMLElements satisfying the given selector within the given scope
 *
 *  @param {String} selector - CSS selector
 *  @param {HTMLElement} container - scope of search
 *  @return {Array} - result of search
 */
export const $$ = (selector, container = document) => Array.from(container.querySelectorAll(selector));

/**
 * Original: https://davidwalsh.name/javascript-loader
 * DAM-2198: Added optional id and target container parameters
 *
 * Generates an HTML element (one of 3 tags) and appends it to the DOM based on
 * the relative options objects defined below this function definition.
 *
 * @param {String} tag - one of: link, script, img
 * @param {String} url - url for src or href attribute
 * @param {String} [id] - id prop for the generated tag
 * @param {String} [container] - overrides append location in options objects
 *
 * @returns {String} a cache ID to avoid duplicate tags
 */
export const loadTag = (tag, url, id = null, container = null) => {
    /* istanbul ignore next (code coverage) */
    id = id ? id : `vf_${hashArray([ tag, url ])}`;

    loadTag.cache[id] = loadTag.cache[id] || new Promise((resolve, reject) => {
        const options = loadTag[tag];
        const el = document.createElement(tag);
        el.onload = () => resolve(el);
        el.onerror = () => reject(el);
        Object.assign(el, options.el, {
            id,
            [options.attr]: url,
        });
        /* istanbul ignore next (code coverage) */
        container = container ? container : options.parent;
        document.querySelector(container).appendChild(el);
    });

    return loadTag.cache[id];
};

loadTag.cache = {};

loadTag.link = {
    parent: 'head',
    attr: 'href',
    el: {
        type: 'text/css',
        rel: 'stylesheet',
    },
};

loadTag.script = {
    parent: 'body',
    attr: 'src',
    el: {
        async: true,
        defer: true,
    },
};

loadTag.img = {
    parent: 'body',
    attr: 'src',
    el: {},
};

export const loadCss = (url) => loadTag('link', url);
// DAM-2198: Added optional id and target container parameters
export const loadScript = (url, id = null, container = null) =>
    loadTag('script', url, id, container);
export const loadImage = (url) => loadTag('img', url);
